import { ReactNode, useMemo, useState } from 'react';
import { Box, Button, Container, Grid, Stack, SvgIcon, Tab, Tabs, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { KpiCard } from './kpi-card';
import { OverviewTab } from './tabs/overview.tab';
import { SalesTab } from './tabs/sales.tab';
import { CatalogTab } from './tabs/catalog.tab';
import { StaffTab } from './tabs/staff.tab';
import { InventoryTab } from './tabs/inventory.tab';
import { inr, num, pct } from './report-theme';
import { averageOrderValue, delta, repeatRate, ReportModel, totalDiscount } from './report-data';
import { neutral } from '../../theme/colors';

interface ReportViewProps {
	report: ReportModel;
	shopName?: string;
	from: Date;
	to: Date;
	/** The filter row. Owned by the page so the view stays presentational. */
	filterBar: ReactNode;
	/** True while a new slice is loading over an existing one. */
	refreshing?: boolean;
	onExport: () => void;
	exportDisabled?: boolean;
	onViewAllOrders: () => void;
}

export const ReportView = ({
	report,
	shopName,
	from,
	to,
	filterBar,
	refreshing = false,
	onExport,
	exportDisabled = false,
	onViewAllOrders,
}: ReportViewProps) => {
	const [tab, setTab] = useState('overview');

	/* Empty trend and staff arrays are how the API signals a plan without advanced analytics. */
	const hasAdvancedAnalytics = report.dailySales.length > 0 || report.staffPerformance.length > 0;

	const aov = averageOrderValue(report);
	const discount = totalDiscount(report);
	const salesDelta =
		report.comparisonMetrics?.monthOverMonth ??
		delta(report.totalSales, report.comparisonMetrics?.previousPeriodSales);
	const spark = report.dailySales.map((point) => point.value);
	const daysInRange = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86_400_000));

	const tabs = useMemo(() => {
		const base = [
			{ value: 'overview', label: 'Overview' },
			{ value: 'sales', label: 'Sales' },
		];
		if (!hasAdvancedAnalytics) return base;
		return [
			...base,
			{ value: 'services', label: 'Services' },
			{ value: 'products', label: 'Products' },
			{ value: 'staff', label: 'Staff' },
			{ value: 'inventory', label: 'Inventory' },
		];
	}, [hasAdvancedAnalytics]);

	const activeTab = tabs.some((entry) => entry.value === tab) ? tab : 'overview';

	return (
		<Box component="main" sx={{ flexGrow: 1, py: 5, backgroundColor: neutral[50], minHeight: '100%' }}>
			<Container maxWidth="xl">
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					justifyContent="space-between"
					spacing={2}
					sx={{ mb: 3 }}
				>
					<Box>
						<Typography variant="h4" sx={{ fontWeight: 600 }}>
							Reports
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{shopName ? `${shopName} · ` : ''}
							{`${from.toLocaleDateString('en-IN')} – ${to.toLocaleDateString('en-IN')}`}
						</Typography>
					</Box>
					<Button
						variant="outlined"
						startIcon={<SvgIcon><DownloadIcon /></SvgIcon>}
						onClick={onExport}
						disabled={exportDisabled}
					>
						Export CSV
					</Button>
				</Stack>

				{/* One filter row, scoping everything below it. */}
				{filterBar}

				{/* Headline numbers stay pinned above the tabs, so drilling in never loses them. */}
				<Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
					<Grid item xs={12} sm={6} md={4} lg={2}>
						<KpiCard
							label="Total sales"
							value={inr(report.totalSales)}
							deltaPercent={salesDelta}
							footnote={
								report.comparisonMetrics?.previousPeriodSales !== undefined
									? `Previous: ${inr(report.comparisonMetrics.previousPeriodSales)}`
									: undefined
							}
							spark={spark}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={2}>
						<KpiCard
							label="Orders"
							value={num(report.orders.length)}
							footnote={
								!report.orders.length
									? 'No orders in this period'
									: daysInRange > 1
										? `≈ ${(report.orders.length / daysInRange).toFixed(1)} per day`
										: undefined
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={2}>
						<KpiCard
							label="Avg order value"
							value={inr(aov)}
							footnote={discount > 0 ? `${inr(discount)} given in discounts` : undefined}
							help="Total sales divided by the number of orders in the selected period."
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={2}>
						<KpiCard
							label="Service sales"
							value={inr(report.totalServiceSales)}
							footnote={
								report.totalSales
									? `${pct((report.totalServiceSales / report.totalSales) * 100, 0)} of takings`
									: undefined
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={2}>
						<KpiCard
							label="Product sales"
							value={inr(report.totalProductSales)}
							footnote={
								report.totalSales
									? `${pct((report.totalProductSales / report.totalSales) * 100, 0)} of takings`
									: undefined
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={2}>
						<KpiCard
							label="Customers"
							value={num(report.totalCustomers)}
							footnote={
								report.totalCustomers
									? `${num(report.totalNewCustomers)} new · ${pct(repeatRate(report), 0)} returning`
									: undefined
							}
						/>
					</Grid>
				</Grid>

				<Box sx={{ borderBottom: `1px solid ${neutral[200]}`, mb: 3 }}>
					<Tabs
						value={activeTab}
						onChange={(_event, value) => setTab(value)}
						variant="scrollable"
						scrollButtons={false}
						sx={{
							minHeight: 44,
							'& .MuiTab-root': {
								textTransform: 'none',
								fontSize: 15,
								fontWeight: 500,
								minHeight: 44,
								px: 2,
							},
						}}
					>
						{tabs.map((entry) => (
							<Tab key={entry.value} value={entry.value} label={entry.label} />
						))}
					</Tabs>
				</Box>

				{/* Refetches dim the current slice rather than replacing it — no layout jump. */}
				<Box sx={{ opacity: refreshing ? 0.55 : 1, transition: 'opacity 150ms' }}>
					{activeTab === 'overview' && (
						<OverviewTab report={report} hasAdvancedAnalytics={hasAdvancedAnalytics} />
					)}
					{activeTab === 'sales' && (
						<SalesTab
							report={report}
							hasAdvancedAnalytics={hasAdvancedAnalytics}
							onViewAllOrders={onViewAllOrders}
						/>
					)}
					{activeTab === 'services' && <CatalogTab report={report} kind="service" />}
					{activeTab === 'products' && <CatalogTab report={report} kind="product" />}
					{activeTab === 'staff' && <StaffTab report={report} />}
					{activeTab === 'inventory' && <InventoryTab report={report} />}
				</Box>
			</Container>
		</Box>
	);
};
